import { ApiError } from '@jinzhuan/api-client';

/**
 * 客户 H5/PWA 统一错误分类（Issue #198）。
 *
 * 所有调用 me/* 的 hook 应通过 classifyMeFetchError 把底层异常映射成
 * 客户可读的提示，避免在 UI 中暴露 HTTP 状态码或后端错误细节。
 *
 * 红线：
 *   - 不在 message 中包含 token、签名 URL、内部错误堆栈。
 *   - 不在 message 中包含手机号、身份证、健康指标等 PII。
 *   - 403 / 404 与 409 必须区分，UI 层根据 kind 做交互（重新登录/刷新/联系顾问）。
 */
export type MeFetchErrorKind =
  | 'session_expired'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'network'
  | 'unknown';

export interface MeFetchError {
  kind: MeFetchErrorKind;
  message: string;
}

const DEFAULT_MESSAGES: Record<MeFetchErrorKind, string> = {
  session_expired: '登录状态已失效，请重新登录',
  forbidden: '当前账号无法访问该数据',
  not_found: '数据暂未生成，稍后再试',
  conflict: '数据已变更，请刷新后再试',
  network: '网络异常，请检查连接后重试',
  unknown: '请求失败，请稍后重试',
};

export function meError(kind: MeFetchErrorKind, message?: string): MeFetchError {
  return { kind, message: message ?? DEFAULT_MESSAGES[kind] };
}

export function classifyMeFetchError(err: unknown): MeFetchError {
  if (err instanceof ApiError) {
    if (err.status === 401) return meError('session_expired');
    if (err.status === 403) return meError('forbidden');
    if (err.status === 404) return meError('not_found');
    if (err.status === 409) return meError('conflict');
    return meError('unknown');
  }
  if (err instanceof Error) {
    return meError('network');
  }
  return meError('unknown');
}

export function isMeFetchError(value: unknown): value is MeFetchError {
  if (!value || typeof value !== 'object') return false;
  const obj = value as { kind?: unknown; message?: unknown };
  return (
    typeof obj.kind === 'string' &&
    typeof obj.message === 'string' &&
    ['session_expired', 'forbidden', 'not_found', 'conflict', 'network', 'unknown'].includes(
      obj.kind,
    )
  );
}
